import { AttemptedKillEntry } from "../types/AttemptedKillEntry";

/**
 * Use mock attempted-kill data until a backend kill index is available.
 * Set to `false` to load from the API instead.
 */
export const MOCK_ATTEMPTED_KILLS_ENABLED = true;

/** Format a bullet count with thousands separators (no currency). */
export const formatAttemptedKillBullets = (value: number): string =>
  value.toLocaleString("en-US");

/** Format a health amount with thousands separators. */
export const formatAttemptedKillHealth = (value: number): string =>
  value.toLocaleString("en-US");

/** Labels for the two participants in an attempt. */
export const ATTEMPTED_KILL_PLAYER_LABELS = {
  attacker: "Attacker",
  target: "Target",
} as const;

export type AttemptedKillPlayer = keyof typeof ATTEMPTED_KILL_PLAYER_LABELS;

export interface AttemptedKillStatLine {
  player: AttemptedKillPlayer;
  label: string;
  value: number;
}

/**
 * Bullets spent per player. With backfire, the target can also fire back, so
 * both the attacker's and target's bullet spend may be shown.
 * Falls back to the legacy single `bullets` value as the attacker's spend.
 */
export const getAttemptedKillBulletLines = (
  entry: AttemptedKillEntry
): AttemptedKillStatLine[] => {
  const attacker = entry.attackerBullets ?? entry.bullets;
  const target = entry.targetBullets;

  const lines: AttemptedKillStatLine[] = [];
  if (attacker !== undefined) {
    lines.push({
      player: "attacker",
      label: ATTEMPTED_KILL_PLAYER_LABELS.attacker,
      value: attacker,
    });
  }
  if (target !== undefined) {
    lines.push({
      player: "target",
      label: ATTEMPTED_KILL_PLAYER_LABELS.target,
      value: target,
    });
  }
  return lines;
};

/**
 * Health lost per player. The target loses health from the attack; with
 * backfire the attacker can lose health too.
 * Falls back to the legacy single `healthLost` value as the target's loss.
 */
export const getAttemptedKillHealthLines = (
  entry: AttemptedKillEntry
): AttemptedKillStatLine[] => {
  const attacker = entry.attackerHealthLost;
  const target = entry.targetHealthLost ?? entry.healthLost;

  const lines: AttemptedKillStatLine[] = [];
  if (attacker !== undefined) {
    lines.push({
      player: "attacker",
      label: ATTEMPTED_KILL_PLAYER_LABELS.attacker,
      value: attacker,
    });
  }
  if (target !== undefined) {
    lines.push({
      player: "target",
      label: ATTEMPTED_KILL_PLAYER_LABELS.target,
      value: target,
    });
  }
  return lines;
};

const hoursAgo = (hours: number): number =>
  Math.floor(Date.now() / 1000) - hours * 3600;

/** Sample failed kill attempts for UI development. */
export const MOCK_ATTEMPTED_KILL_ENTRIES: AttemptedKillEntry[] = [
  {
    id: "mock-attempt-1",
    timestamp: hoursAgo(1),
    attackerAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    victimAddress: "0x8ba1f109551bD432803012645Ac136c22C177e9",
    attackerName: "AldoVescari",
    victimName: "PaulieWalnuts",
    attackerBullets: 9000,
    targetHealthLost: 35,
    transactionHash: "0xmockfail000000000000000000000000000000000000000000000000000000001",
    blockNumber: 42100200,
  },
  {
    id: "mock-attempt-2",
    timestamp: hoursAgo(5),
    attackerAddress: "0x3f5CE5FBFe3E9af3971dD823D1523B0aC24c131a",
    victimAddress: "0x1a2b3c4d5e6f708192a3b4c5d6e7f8091a2b3c4d5",
    attackerName: "VitoCorleone",
    victimName: "Salvatore",
    attackerBullets: 25000,
    targetBullets: 12000,
    attackerHealthLost: 20,
    targetHealthLost: 60,
    transactionHash: "0xmockfail000000000000000000000000000000000000000000000000000000002",
    blockNumber: 42099900,
  },
  {
    id: "mock-attempt-3",
    timestamp: hoursAgo(11),
    attackerAddress: "0x5aAeb6053F3E94C9b9A09f33669435E7Ef1BeAed",
    victimAddress: "0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb0",
    attackerName: "NicoBellandi",
    victimName: "AldoVescari",
    attackerBullets: 14000,
    targetHealthLost: 48,
    transactionHash: "0xmockfail000000000000000000000000000000000000000000000000000000003",
    blockNumber: 42099550,
  },
  {
    id: "mock-attempt-4",
    timestamp: hoursAgo(20),
    attackerAddress: "0x8626f6940E2eb28930eFb4CeF49B2d1F2C9C1199",
    victimAddress: "0x2546BcD3c84621e976D8185Aa27800a256bD1eC3",
    attackerName: "Silvio",
    victimName: "Adriana",
    attackerBullets: 1200,
    targetHealthLost: 8,
    transactionHash: "0xmockfail000000000000000000000000000000000000000000000000000000004",
    blockNumber: 42098850,
  },
  {
    id: "mock-attempt-5",
    timestamp: hoursAgo(30),
    attackerAddress: "0x1a2b3c4d5e6f708192a3b4c5d6e7f8091a2b3c4d5",
    victimAddress: "0x3f5CE5FBFe3E9af3971dD823D1523B0aC24c131a",
    attackerName: "Salvatore",
    victimName: "VitoCorleone",
    attackerBullets: 0,
    targetHealthLost: 0,
    transactionHash: "0xmockfail000000000000000000000000000000000000000000000000000000005",
    blockNumber: 42098150,
  },
  {
    id: "mock-attempt-6",
    timestamp: hoursAgo(44),
    attackerAddress: "0x8ba1f109551bD432803012645Ac136c22C177e9",
    victimAddress: "0x5aAeb6053F3E94C9b9A09f33669435E7Ef1BeAed",
    attackerName: "PaulieWalnuts",
    victimName: "NicoBellandi",
    attackerBullets: 31000,
    targetBullets: 31000,
    attackerHealthLost: 50,
    targetHealthLost: 72,
    transactionHash: "0xmockfail000000000000000000000000000000000000000000000000000000006",
    blockNumber: 42097250,
  },
];
